import { useState, useEffect } from 'react';

/**
 * Resolved icon components, keyed by PascalCase name.
 *
 * Without this, every `<Icon>` mount re-awaited `import('iconoir-react')` and rendered `null` for
 * a frame or more. That is invisible for static icons, but it made short-lived elements — a toast
 * remounted on each show, for instance — appear with no icon at all before they dismissed.
 */
const iconCache = new Map<string, unknown>();

/** In-flight import, shared so concurrent first mounts don't each trigger their own. */
let iconModulePromise: Promise<Record<string, unknown>> | null = null;

function loadIconModule(): Promise<Record<string, unknown>> {
  iconModulePromise ??= import('iconoir-react') as unknown as Promise<Record<string, unknown>>;
  return iconModulePromise;
}

/**
 * Converts kebab-case icon names to PascalCase component names
 * @param kebabCase - Icon name in kebab-case (e.g., 'info-circle-solid', 'airplane-helix-45deg')
 * @returns PascalCase component name (e.g., 'InfoCircleSolid', 'AirplaneHelix45deg')
 * @example
 * ```tsx
 * kebabToPascalCase('info-circle-solid') // Returns 'InfoCircleSolid'
 * kebabToPascalCase('airplane-helix-45deg') // Returns 'AirplaneHelix45deg'
 * ```
 */
function kebabToPascalCase(kebabCase: string): string {
  return kebabCase
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

/**
 * Custom hook to dynamically load and render iconoir-react icons
 * @param iconName - The name of the icon in either kebab-case or PascalCase
 *                   - Kebab-case: 'info-circle-solid', 'check-circle-solid', 'airplane-helix-45deg'
 *                   - PascalCase: 'InfoCircleSolid', 'CheckCircleSolid', 'AirplaneHelix45deg'
 * @returns The loaded icon component or null if not yet loaded
 *
 * @example
 * ```tsx
 * // Using kebab-case (recommended)
 * const IconComponent = useDynamicIcon('info-circle-solid');
 *
 * // Using PascalCase (also supported)
 * const IconComponent = useDynamicIcon('InfoCircleSolid');
 *
 * return IconComponent ? <IconComponent width={24} height={24} /> : null;
 * ```
 */
export function useDynamicIcon(iconName: string) {
  // Seed from the cache so an already-resolved icon renders on the first paint rather than after
  // an effect — remounted, short-lived elements would otherwise show nothing.
  const [IconComponent, setIconComponent] = useState<any>(
    () => iconCache.get(kebabToPascalCase(iconName)) ?? null
  );

  useEffect(() => {
    if (!iconName) {
      setIconComponent(null);
      return;
    }

    // Convert kebab-case to PascalCase to match iconoir-react component names
    const componentName = kebabToPascalCase(iconName);

    const cached = iconCache.get(componentName);
    if (cached) {
      setIconComponent(() => cached);
      return;
    }

    let isStale = false;

    loadIconModule()
      .then(module => {
        const icon = module[componentName];
        if (icon) {
          iconCache.set(componentName, icon);
          if (!isStale) setIconComponent(() => icon);
        } else {
          console.warn(`Icon "${iconName}" (component: "${componentName}") not found in iconoir-react`);
          if (!isStale) setIconComponent(null);
        }
      })
      .catch(error => {
        console.error(`Failed to load icon "${iconName}":`, error);
        if (!isStale) setIconComponent(null);
      });

    return () => {
      isStale = true;
    };
  }, [iconName]);

  return IconComponent;
}
